import { Suspense, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import cx from 'classnames';

import { usePingMutation } from '@greenkub-fronts/data-access';
import {
  BannerStaging,
  Snackbar,
  ProtectedRoute,
  Modal,
  Button,
  Icon,
  IconEnum,
  LoaderCube,
} from '@greenkub-fronts/shared/ui';

import { PageLogin } from '@greenkub-fronts/pages/login';
import { RootState, UiTheme } from '@greenkub-fronts/shared/interface';

import { HomeLayout } from './commons/HomeLayout';
import { ROUTER } from './router/main.router';

import { usePostHog } from 'posthog-js/react';
import TagManager from 'react-gtm-module';

export function App() {
  const location = useLocation();
  const navigate = useNavigate();

  const { sideBarIsOpen, currentTheme } = useSelector(
    (state: RootState) => state.ui
  );
  const { role, user } = useSelector((state: RootState) => state.auth);

  const [ping] = usePingMutation();

  const postHog = usePostHog();

  useEffect(() => {
    if (
      location.pathname.includes('/signature/') &&
      !location.pathname.includes('/sign/') &&
      !location.pathname.includes('prepare') &&
      !location.pathname.includes('success')
    ) {
      const newLocation = location.pathname.replace(
        '/signature/',
        '/signature/sign/'
      );
      navigate(newLocation);
    }
  }, [location]);

  const logoutAndRedirectToCustomerArea = () => {
    localStorage.removeItem('auth');
    window.location.replace(import.meta.env.VITE_CUSTOMER_AREA_URL + '');
  };

  const pingHandler = useCallback(() => {
    if (user) {
      ping();
    }
  }, [user]);

  // Ping Activity
  useEffect(() => {
    pingHandler();
    let interval: any = null;
    interval = setInterval(ping, 1000 * 60 * 5); // toutes les 5mn
    return () => {
      clearInterval(interval);
    };
  }, [ping]);

  // PostHog analytics
  useEffect(() => {
    if (user) {
      postHog?.identify(user.id, {
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        role: role,
      });
    } else {
      postHog?.reset();
    }
  }, [postHog, role, user]);

  // GTM analytics
  useEffect(() => {
    TagManager.dataLayer({
      dataLayer: {
        event: 'page_view',
        pagePath: location.pathname,
        userId: user?.id,
      },
    });
  }, [location.pathname, user?.id]);

  /**
   * REDIRECTION SI CONNECTE EN TEMPS QUE CLIENT
   */
  if (role === 'CUSTOMER') {
    return (
      <Modal className={'RedirectModal'} isOpened={true}>
        <div>
          <Icon name={IconEnum.LOGO} />

          <h1>Vous souhaitez accéder à votre espace client Greenkub</h1>

          <p>Votre espace client a deménagé à l'adresse suivante</p>

          <p>
            <strong>{import.meta.env.VITE_CUSTOMER_AREA_URL}</strong>
          </p>

          <Button onClick={() => logoutAndRedirectToCustomerArea()}>
            <span>
              Je pars m'authentifier sur l'espace client{' '}
              <Icon name={IconEnum.ARROW_FORWARD_IOS_ROUNDED} />
            </span>
          </Button>
        </div>
      </Modal>
    );
  }

  return (
    <>
      <BannerStaging />

      <Snackbar />

      <Routes location={location}>
        <Route path={'login/*'} element={<PageLogin />} />

        {ROUTER.map(
          (route) =>
            !route.protected && (
              <Route
                key={route.path}
                path={route.path}
                element={
                  <Suspense fallback={<LoaderCube isLoading={true} />}>
                    {route.component}
                  </Suspense>
                }
              />
            )
        )}

        {ROUTER.map(
          (route) =>
            route.protected && (
              <Route
                key={route.path}
                path={route.path}
                element={
                  !route.layout ? (
                    <ProtectedRoute>
                      <Suspense fallback={<LoaderCube isLoading={true} />}>
                        {route.component}
                      </Suspense>
                    </ProtectedRoute>
                  ) : (
                    <div
                      className={cx(
                        'Home',
                        sideBarIsOpen && currentTheme !== UiTheme.GEKA
                          ? 'sidebarIsOpen'
                          : 'sidebarIsClosed'
                      )}
                    >
                      <ProtectedRoute>
                        <HomeLayout
                          withProjectNavigationBar={
                            route.withProjectNavigationBar
                          }
                        >
                          <Suspense fallback={<LoaderCube isLoading={true} />}>
                            {route.component}
                          </Suspense>
                        </HomeLayout>
                      </ProtectedRoute>
                    </div>
                  )
                }
              />
            )
        )}
      </Routes>
    </>
  );
}

export default App;
